import http from 'helpers/requests';
import {
  SupplierApiError,
  mapSupplierApiError,
  supplierRequestAndParse,
} from './supplierApiPrimitives';

// Authenticated get/post/patch/put-then-zod-parse request helpers for the
// free-supplier domain APIs (supplierFactorApi, supplierDraftApi,
// supplierUploadApi, supplierInboxApi). Each domain keeps its OWN endpoint
// map, multipart construction, and schemas — this module owns only the
// plumbing that was byte-identical across all four, built on the AUTHENTICATED
// `http` client.
//
// Per-domain error classes (SupplierFactorApiError, SupplierDraftApiError, …)
// extend SupplierApiError rather than duplicating its constructor, so pages'
// `instanceof SupplierDraftApiError` etc. checks keep working exactly as
// before (B2) — `instanceof SupplierApiError` also now holds for all of them.
// invitationApi.js is deliberately NOT folded in here — it authenticates
// differently (http.publicPost), has no endpoint map, and maps its errors by
// parsing a schema rather than a generic "response.data is an object" check.
//
// S3: SupplierApiError/mapSupplierApiError/supplierRequestAndParse moved to
// supplierApiPrimitives.js (transport-neutral — no `http` import) and are
// re-exported below so every existing importer's combined import statement
// (these three alongside the authenticated adapters here) keeps working
// unchanged.

export { SupplierApiError, mapSupplierApiError, supplierRequestAndParse };

export const supplierGetAndParse = (endpoint, schema, mapApiError) =>
  supplierRequestAndParse(() => http.get(endpoint), schema, mapApiError);

export const supplierPostAndParse = (endpoint, body, schema, mapApiError) =>
  supplierRequestAndParse(() => http.post(endpoint, body), schema, mapApiError);

export const supplierPatchAndParse = (endpoint, body, schema, mapApiError) =>
  supplierRequestAndParse(() => http.patch(endpoint, body), schema, mapApiError);

export const supplierPutAndParse = (endpoint, body, schema, mapApiError) =>
  supplierRequestAndParse(() => http.put(endpoint, body), schema, mapApiError);

// A pass-through "schema" (same `.parse` shape zod schemas expose) for the
// one supplier response that mirrors a LEGACY, un-contracted payload
// (GET /supplier/diagrams/:id — see supplierDiagramApi.js) rather than an
// S5/S6 versioned V1 contract. Composes with supplierRequestAndParse instead
// of duplicating its try/catch; callers must read the result DEFENSIVELY
// (optional chaining/fallbacks) — inventing a zod schema for a legacy shape
// we don't own would just be a second, silently-drifting copy of it.
const identitySchema = { parse: (data) => data };

export const supplierGetRaw = (endpoint, mapApiError) =>
  supplierRequestAndParse(() => http.get(endpoint), identitySchema, mapApiError);
